import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import logo from '../Header/Images/RMB-Strip-Jaipur.png';
import { FaUser, FaBuilding, FaPhone, FaEnvelope, FaQrcode, FaSearch, FaSave, FaHandshake, FaSignOutAlt, FaShieldAlt } from 'react-icons/fa';

interface MemberItem {
  id: number;
  name: string;
  email: string;
  number: string;
  company_name: string;
  designation: string;
  business_classification?: string;
  company_address?: string;
  profile?: string;
}

const VisitorPortal: React.FC = () => {
  const navigate = useNavigate();
  const [visitor, setVisitor] = useState<any>(null);
  const [members, setMembers] = useState<MemberItem[]>([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [loading, setLoading] = useState(true);

  // Active view: 'directory' | 'scan' | 'my_pass'
  const [activeTab, setActiveTab] = useState<'directory' | 'scan' | 'my_pass'>('directory');

  const [scanMemberId, setScanMemberId] = useState('');
  const [scanMsg, setScanMsg] = useState('');
  const [scanLoading, setScanLoading] = useState(false);

  useEffect(() => {
    const userStr = localStorage.getItem('user');
    if (userStr) {
      try {
        const u = JSON.parse(userStr);
        setVisitor(u);
      } catch { }
    }

    const fetchMembers = async () => {
      try {
        const res = await axios.get('https://rmbbackend.rmbjaipur.com/auth/allusersinfo');
        const list = Array.isArray(res.data) ? res.data : (res.data?.users || []);
        setMembers(list.filter((m: any) => m.role !== 'visitor'));
      } catch (e) {
        console.error('Error fetching members:', e);
      } finally {
        setLoading(false);
      }
    };

    fetchMembers();
  }, []);

  const handleLogout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    navigate('/login');
  };

  const handleConnectMember = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!scanMemberId || !visitor?.id) return;
    setScanLoading(true);
    setScanMsg('');

    try {
      const res = await axios.post('https://rmbbackend.rmbjaipur.com/auth/member/connect', {
        scanned_by_user_id: visitor.id,
        scanned_user_id: parseInt(scanMemberId, 10)
      });
      if (res.data.success) {
        setScanMsg(`✅ Connected with ${res.data.member?.name || 'Member'}! Downloading vCard contact...`);
        window.location.href = `https://rmbbackend.rmbjaipur.com/auth/member/vcard/${scanMemberId}`;
        setScanMemberId('');
      }
    } catch {
      setScanMsg('❌ Member not found or invalid QR content.');
    } finally {
      setScanLoading(false);
    }
  };

  const filteredMembers = members.filter(m => {
    const q = searchQuery.toLowerCase();
    return (
      (m.name || '').toLowerCase().includes(q) ||
      (m.company_name || '').toLowerCase().includes(q) ||
      (m.business_classification || '').toLowerCase().includes(q) ||
      (m.designation || '').toLowerCase().includes(q)
    );
  });

  return (
    <div className="min-h-screen bg-[#070D18] text-slate-100 flex flex-col">

      {/* Visitor Top Header */}
      <header className="bg-[#0A2451] border-b border-[#1E2B45] px-4 py-3 sticky top-0 z-40 shadow-xl flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="bg-white px-2.5 py-1.5 rounded-xl shadow-md">
            <img src={logo} alt="RMB Jaipur" className="h-7 w-auto object-contain" />
          </div>
          <div>
            <h1 className="font-extrabold text-sm sm:text-base text-white flex items-center gap-2">
              👋 Guest Visitor Portal
            </h1>
            <p className="text-[10px] text-amber-400 font-medium">Welcome, {visitor?.name || 'Guest Visitor'}</p>
          </div>
        </div>

        <div className="flex items-center gap-2">
          <button
            onClick={handleLogout}
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-xl bg-rose-500/10 hover:bg-rose-500/20 border border-rose-500/30 text-rose-400 font-bold text-xs transition"
          >
            <FaSignOutAlt /> Sign Out
          </button>
        </div>
      </header>

      {/* Main Container */}
      <div className="flex-1 max-w-7xl w-full mx-auto p-4 sm:p-6 space-y-6">

        {/* Navigation Ribbon */}
        <div className="flex flex-wrap items-center justify-between gap-3 bg-[#0f1b2d] p-3 rounded-2xl border border-[#1E2B45] shadow-lg">
          <div className="flex items-center gap-2">
            <button
              onClick={() => setActiveTab('directory')}
              className={`px-4 py-2 rounded-xl text-xs font-black transition cursor-pointer flex items-center gap-2 ${activeTab === 'directory' ? 'bg-[#FDBC12] text-slate-950 shadow-md' : 'text-slate-300 hover:bg-slate-800'}`}
            >
              <FaUser /> Member Directory ({members.length})
            </button>
            <button
              onClick={() => setActiveTab('scan')}
              className={`px-4 py-2 rounded-xl text-xs font-black transition cursor-pointer flex items-center gap-2 ${activeTab === 'scan' ? 'bg-[#FDBC12] text-slate-950 shadow-md' : 'text-slate-300 hover:bg-slate-800'}`}
            >
              <FaQrcode /> Scan Member QR & Save Contact
            </button>
            <button
              onClick={() => setActiveTab('my_pass')}
              className={`px-4 py-2 rounded-xl text-xs font-black transition cursor-pointer flex items-center gap-2 ${activeTab === 'my_pass' ? 'bg-[#FDBC12] text-slate-950 shadow-md' : 'text-slate-300 hover:bg-slate-800'}`}
            >
              <FaShieldAlt /> My Guest Pass
            </button>
          </div>

          <span className="text-[11px] font-mono text-slate-400 bg-slate-800 px-3 py-1 rounded-full border border-slate-700">
            Visitor Pass Active
          </span>
        </div>

        {/* TAB 1: MEMBER DIRECTORY */}
        {activeTab === 'directory' && (
          <div className="space-y-4">

            {/* Search Input */}
            <div className="relative max-w-md">
              <FaSearch className="absolute left-3.5 top-3.5 text-slate-400 text-xs" />
              <input
                type="text"
                placeholder="Search members by name, company, or business classification..."
                value={searchQuery}
                onChange={e => setSearchQuery(e.target.value)}
                className="w-full pl-9 pr-4 py-2.5 rounded-xl bg-[#0f1b2d] border border-[#1E2B45] text-xs text-white focus:outline-none focus:border-amber-400"
              />
            </div>

            {loading ? (
              <div className="py-20 text-center text-slate-400 text-sm">Loading chapter members...</div>
            ) : filteredMembers.length === 0 ? (
              <div className="py-20 text-center text-slate-400 text-sm">No members found.</div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {filteredMembers.map(m => (
                  <div key={m.id} className="p-4 rounded-2xl bg-[#0f1b2d] border border-[#1E2B45] hover:border-amber-400/50 transition shadow-md flex flex-col justify-between">
                    <div>
                      <div className="flex items-center gap-3 mb-3">
                        <img
                          src={m.profile ? (m.profile.startsWith('http') ? m.profile : `https://rmbbackend.rmbjaipur.com/${m.profile}`) : 'https://via.placeholder.com/150'}
                          alt={m.name}
                          className="w-12 h-12 rounded-full object-cover border-2 border-amber-400/30"
                        />
                        <div>
                          <h3 className="font-bold text-sm text-white">{m.name}</h3>
                          <p className="text-[11px] text-amber-400 font-semibold">{m.business_classification || m.designation || 'Rotary Member'}</p>
                        </div>
                      </div>

                      <div className="space-y-1.5 text-xs text-slate-300 py-2 border-t border-slate-800">
                        {m.company_name && <div className="flex items-center gap-2"><FaBuilding className="text-slate-500 text-[10px]" /> <span className="font-semibold">{m.company_name}</span></div>}
                        {m.number && <div className="flex items-center gap-2"><FaPhone className="text-slate-500 text-[10px]" /> <span>{m.number}</span></div>}
                        {m.email && <div className="flex items-center gap-2 truncate"><FaEnvelope className="text-slate-500 text-[10px]" /> <span className="truncate">{m.email}</span></div>}
                      </div>
                    </div>

                    <a
                      href={`https://rmbbackend.rmbjaipur.com/auth/member/vcard/${m.id}`}
                      download={`${(m.name || 'member').replace(/[^a-zA-Z0-9]/g, '_')}.vcf`}
                      className="mt-3 py-2 px-3 bg-amber-500/10 hover:bg-amber-500/20 border border-amber-500/30 text-amber-400 font-bold text-xs rounded-xl flex items-center justify-center gap-2 transition"
                    >
                      <FaSave /> Save Contact (.vcf)
                    </a>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* TAB 2: SCAN MEMBER QR */}
        {activeTab === 'scan' && (
          <div className="max-w-xl mx-auto p-6 rounded-2xl bg-[#0f1b2d] border border-[#1E2B45] shadow-xl text-center space-y-4">
            <div className="w-14 h-14 rounded-full bg-amber-500/10 text-amber-400 flex items-center justify-center text-2xl mx-auto">
              <FaQrcode />
            </div>
            <h2 className="text-xl font-black text-white">Scan & Save Member Contact</h2>
            <p className="text-xs text-slate-400">
              Enter or paste a member's ID/QR code link to save their full contact info directly into your phone.
            </p>

            <form onSubmit={handleConnectMember} className="space-y-4 text-left">
              <div>
                <label className="block text-xs font-bold text-slate-300 mb-1">Member ID / QR Code Payload</label>
                <input
                  type="text"
                  placeholder="e.g. 10 or paste QR link"
                  value={scanMemberId}
                  onChange={(e) => {
                    const val = e.target.value;
                    const match = val.match(/\/vcard\/(\d+)/) || val.match(/ID:?\s*#?(\d+)/) || [null, val];
                    setScanMemberId(match[1] || val);
                  }}
                  className="w-full px-4 py-3 rounded-xl bg-[#070D18] border border-[#1E2B45] text-xs focus:outline-none focus:border-amber-400 font-mono text-amber-400"
                />
              </div>

              <button
                type="submit"
                disabled={scanLoading || !scanMemberId}
                className="w-full py-3 bg-gradient-to-r from-amber-500 to-amber-600 hover:from-amber-600 hover:to-amber-700 disabled:opacity-50 text-slate-950 font-black text-xs rounded-xl shadow-lg flex items-center justify-center gap-2 transition cursor-pointer"
              >
                {scanLoading ? 'Processing...' : '📱 Connect & Download Contact (.vcf)'}
              </button>
            </form>

            {scanMsg && (
              <div className={`p-3 rounded-xl text-xs font-semibold ${scanMsg.startsWith('✅') ? 'bg-emerald-500/10 border border-emerald-500/30 text-emerald-400' : 'bg-rose-500/10 border border-rose-500/30 text-rose-400'}`}>
                {scanMsg}
              </div>
            )}
          </div>
        )}

        {/* TAB 3: MY GUEST PASS */}
        {activeTab === 'my_pass' && visitor && (
          <div className="max-w-md mx-auto p-6 rounded-2xl bg-[#0f1b2d] border border-[#1E2B45] shadow-xl text-center space-y-4">
            <span className="text-xs font-bold uppercase tracking-wider text-amber-400">Visitor Event Pass</span>

            <div className="bg-white p-4 rounded-2xl shadow-xl my-3 inline-block">
              <img
                src={`https://api.qrserver.com/v1/create-qr-code/?size=200x200&data=https://rmbbackend.rmbjaipur.com/auth/member/vcard/${visitor.id}`}
                alt="Visitor Pass QR"
                className="w-44 h-44 object-contain mx-auto"
              />
            </div>

            <h3 className="text-lg font-bold text-white">{visitor.name}</h3>
            <p className="text-xs text-slate-400">{visitor.company_name || 'Event Guest Visitor'}</p>
            <p className="text-xs text-amber-400 font-mono">Visitor ID: #{visitor.id}</p>

            <a
              href={`https://rmbbackend.rmbjaipur.com/auth/member/vcard/${visitor.id}`}
              download={`${(visitor.name || 'visitor').replace(/[^a-zA-Z0-9]/g, '_')}.vcf`}
              className="mt-4 w-full py-2.5 bg-slate-800 hover:bg-slate-700 text-white font-bold text-xs rounded-xl shadow border border-slate-700 flex items-center justify-center gap-2 transition"
            >
              <FaHandshake /> Share My Contact (.vcf)
            </a>
          </div>
        )}

      </div>
    </div>
  );
};

export default VisitorPortal;
